import { ActionType } from 'typesafe-actions'
import * as actions from './actions'

export type RelationshipAction =
  | ActionType<typeof actions.relationshipRequest>
  | ActionType<typeof actions.relationshipSuccess>
  | ActionType<typeof actions.relationshipFail>

export type GendersAction =
  | ActionType<typeof actions.genderRequest>
  | ActionType<typeof actions.genderSuccess>
  | ActionType<typeof actions.genderFail>

export type PatientsAction =
  | ActionType<typeof actions.patientsRequest>
  | ActionType<typeof actions.patientsSuccess>
  | ActionType<typeof actions.patientsFail>

export type AppointmentsAction =
  | ActionType<typeof actions.appointmentsRequest>
  | ActionType<typeof actions.appointmentsSuccess>
  | ActionType<typeof actions.appointmentsFail>

export type PhysicianAction =
  | ActionType<typeof actions.physiciansRequest>
  | ActionType<typeof actions.physiciansSuccess>
  | ActionType<typeof actions.physiciansFail>

  export type ContractGroupsAction =
  | ActionType<typeof actions.contractGroupsRequest>
  | ActionType<typeof actions.contractGroupsSuccess>
  | ActionType<typeof actions.contractGroupsFail>

export type ServiceAreasAction =
  | ActionType<typeof actions.serviceAreasRequest>
  | ActionType<typeof actions.serviceAreasSuccess>
  | ActionType<typeof actions.serviceAreasFail>

export type Cpt4CodesAction =
  | ActionType<typeof actions.getCpt4Request>
  | ActionType<typeof actions.getCpt4Success>
  | ActionType<typeof actions.getCpt4Fail>
  | ActionType<typeof actions.createCpt4Request>
  | ActionType<typeof actions.createCpt4Success>
  | ActionType<typeof actions.createCpt4Fail>
  | ActionType<typeof actions.updateCpt4Request>
  | ActionType<typeof actions.updateCpt4Success>
  | ActionType<typeof actions.updateCpt4Fail>
  | ActionType<typeof actions.deleteCpt4Request>
  | ActionType<typeof actions.deleteCpt4Success>
  | ActionType<typeof actions.deleteCpt4Fail>

export type ConfigurationAction =
  | ActionType<typeof actions.configurationRequest>
  | ActionType<typeof actions.configurationSuccess>
  | ActionType<typeof actions.configurationFail>

export type ConfigurationLogoAction =
  | ActionType<typeof actions.configurationLogoRequest>
  | ActionType<typeof actions.configurationLogoSuccess>
  | ActionType<typeof actions.configurationLogoFail>
  | ActionType<typeof actions.configurationLogoHeaderRequest>
  | ActionType<typeof actions.configurationLogoHeaderSuccess>
  | ActionType<typeof actions.configurationHeaderLogoFail>

export interface RelationshipState {
  isFetching: boolean
  data: string[]
}

export interface GenderState {
  isFetching: boolean
  data: string[]
}

export interface PatientState {
  isFetching: boolean
  data: IPatientResponse
}

export enum AppointmentVariant {
  Appointment = 'description',
  CptCode = 'cpt4Code'
}

export interface AppointmentState {
  selected: AppointmentVariant
  isFetching: boolean
  data: IAppointmentResponse
}

export interface PhysicianState {
  isFetching: boolean
  data: {
    content: Array<any>
  }
  error: string | null
}

export interface ContractGroupsState {
  isFetching: boolean
  data: {
    content: Array<string>
  }
  error: string | null
}

export interface ServiceAreasState {
  isFetching: boolean
  data: {
    content: Array<any>
  }
  error: string | null
}

export interface Cpt4CodesState {
  isFetching: boolean
  data: {
    content: Array<any>
    size: number,
    totalPages: number,
    number: number
  },
  errors: {
      fetchingError: boolean;
      creatingError: boolean;
      updatingError: boolean;
      deletingError: boolean;
  }
}

export interface IAppointmentResponse {
  content: Array<{
    id?: number
    numberOfElements?: number
    appointmentCode?: string
    description?: string
  }>
}

export interface IPatientResponse {
  content: Array<{
    id?: number
    carrierName?: number
    requiredMemberId?: boolean
    dobRequired?: boolean
    fakeEligibility?: boolean
  }>
}

export interface ConfigurationLogoState {
  isFetching: boolean
  content: any
  contentHeader: any
}

export interface ConfigurationState {
  isFetching: boolean
  data: {
    appointmentTypeSearchEnabled: boolean
    defaultEstimationDropdownType: string
    estimatorHost: string
    fakeEligibility: boolean
    inPatientEnable: boolean
    logoEnabled: boolean
    outComplexEnable: boolean
    outPatientEnable: boolean
    physicianFieldEnabled: boolean
    procedureCodeEnabled: boolean
    serviceAreaFieldEnabled: boolean
  }
}

export enum RelationshipActionTypes {
  FETCH_RELATIONSHIPS_REQUEST = '@@references/FETCH_RELATIONSHIPS_REQUEST',
  FETCH_RELATIONSHIPS_SUCCESS = '@@references/FETCH_RELATIONSHIPS_SUCCESS',
  FETCH_RELATIONSHIPS_FAIL = '@@references/FETCH_RELATIONSHIPS_FAIL'
}

export enum GendersActionTypes {
  FETCH_GENDERS_REQUEST = '@@references/FETCH_GENDERS_REQUEST',
  FETCH_GENDERS_SUCCESS = '@@references/FETCH_GENDERS_SUCCESS',
  FETCH_GENDERS_FAIL = '@@references/FETCH_GENDERS_FAIL'
}

export enum PatientActionTypes {
  FETCH_PATIENTS_REQUEST = '@@references/FETCH_PATIENTS_REQUEST',
  FETCH_PATIENTS_SUCCESS = '@@references/FETCH_PATIENTS_SUCCESS',
  FETCH_PATIENTS_FAIL = '@@references/FETCH_PATIENTS_FAIL'
}

export enum AppointmentsTypes {
  FETCH_APPOINTMENTS_REQUEST = '@@references/FETCH_APPOINTMENTS_REQUEST',
  FETCH_APPOINTMENTS_SUCCESS = '@@references/FETCH_APPOINTMENTS_SUCCESS',
  FETCH_APPOINTMENTS_FAIL = '@@references/FETCH_APPOINTMENTS_FAIL'
}

export enum PhysicianTypes {
  FETCH_PHYSICIANS_REQUEST = '@@references/FETCH_PHYSICIANS_REQUEST',
  FETCH_PHYSICIANS_SUCCESS = '@@references/FETCH_PHYSICIANS_SUCCESS',
  FETCH_PHYSICIANS_FAIL = '@@references/FETCH_PHYSICIANS_FAIL'
}

export enum ContractGroupsTypes {
  FETCH_CONTRACT_GROUPS_REQUEST = '@@references/FETCH_CONTRACT_GROUPS_REQUEST',
  FETCH_CONTRACT_GROUPS_SUCCESS = '@@references/FETCH_CONTRACT_GROUPS_SUCCESS',
  FETCH_CONTRACT_GROUPS_FAIL = '@@references/FETCH_CONTRACT_GROUPS_FAIL'
}

export enum ServiceAreasTypes {
  FETCH_SERVICES_AREAS_REQUEST = '@@references/FETCH_SERVICES_AREAS_REQUEST',
  FETCH_SERVICES_AREAS_SUCCESS = '@@references/FETCH_SERVICES_AREAS_SUCCESS',
  FETCH_SERVICES_AREAS_FAIL = '@@references/FETCH_SERVICES_AREAS_FAIL'
}

export enum Cpt4CodeTypes {
  FETCH_CPT4_CODES_REQUEST = '@@references/FETCH_CPT4_CODES_REQUEST',
  FETCH_CPT4_CODES_SUCCESS = '@@references/FETCH_CPT4_CODES_SUCCESS',
  FETCH_CPT4_CODES_FAIL = '@@references/FETCH_CPT4_CODES_FAIL',
  CREATE_CPT4_CODES_REQUEST = '@@references/CREATE_CPT4_CODES_REQUEST',
  CREATE_CPT4_CODES_SUCCESS = '@@references/CREATE_CPT4_CODES_SUCCESS',
  CREATE_CPT4_CODES_FAIL = '@@references/CREATE_CPT4_CODES_FAIL',
  UPDATE_CPT4_CODES_REQUEST = '@@references/UPDATE_CPT4_CODES_REQUEST',
  UPDATE_CPT4_CODES_SUCCESS = '@@references/UPDATE_CPT4_CODES_SUCCESS',
  UPDATE_CPT4_CODES_FAIL = '@@references/UPDATE_CPT4_CODES_FAIL',
  DELETE_CPT4_CODES_REQUEST = '@@references/DELETE_CPT4_CODES_REQUEST',
  DELETE_CPT4_CODES_SUCCESS = '@@references/DELETE_CPT4_CODES_SUCCESS',
  DELETE_CPT4_CODES_FAIL = '@@references/DELETE_CPT4_CODES_FAIL'
}

export enum ConfigurationTypes {
  CONFIGURATION_REQUEST = '@@references/CONFIGURATION_REQUEST',
  CONFIGURATION_SUCCESS = '@@references/CONFIGURATION_SUCCESS',
  CONFIGURATION_FAIL = '@@references/CONFIGURATION_FAIL'
}

export enum ConfigurationLogoTypes {
  CONFIGURATION_LOGO_REQUEST = '@@references/CONFIGURATION_LOGO_REQUEST',
  CONFIGURATION_LOGO_SUCCESS = '@@references/CONFIGURATION_LOGO_SUCCESS',
  CONFIGURATION_LOGO_FAIL = '@@references/CONFIGURATION_LOGO_FAIL',
  CONFIGURATION_LOGO_HEADER_REQUEST = '@@references/CONFIGURATION_LOGO_HEADER_REQUEST',
  CONFIGURATION_LOGO_HEADER_SUCCESS = '@@references/CONFIGURATION_LOGO_HEADER_SUCCESS',
  CONFIGURATION_LOGO_HEADER_FAIL = '@@references/CONFIGURATION_HEADER_LOGO_FAIL'
}

export interface ReferencesState {
  relationships: RelationshipState
  genders: GenderState
  patients: PatientState
  appointments: AppointmentState
  physicians: PhysicianState
  contractGroups: ContractGroupsState
  serviceAreas: ServiceAreasState
  cpt4Codes: Cpt4CodesState
  configuration: ConfigurationState
  configurationLogo: ConfigurationLogoState
}

export type ReferencesAction =
  | RelationshipAction
  | GendersAction
  | PatientsAction
  | AppointmentsAction
  | PhysicianAction
  | ServiceAreasAction
  | Cpt4CodesAction
  | ConfigurationAction
  | ConfigurationLogoAction
  | ContractGroupsAction

/*
content: [{id: 1, deleted: false, createdAt: "2019-11-12T22:49:58.222725Z",…},…]
empty: false
first: true
last: false
number: 0
numberOfElements: 20
pageable: {sort: {sorted: false, unsorted: true, empty: true}, pageSize: 20, pageNumber: 0, offset: 0,…}
size: 20
sort: {sorted: false, unsorted: true, empty: true}
totalElements: 255
totalPages: 13
 */
